import { useFormContext, useFieldArray, Controller } from "react-hook-form";
import { Pipeline, OpportunityState } from "../../../types/domain";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Input } from "../../common/Input";
import { ErrorLabel } from "@/components/common/ErrorLabel";
import { validateNoHtml } from "@/utils/fieldValidation";

type PipelineStagesTableProps = {
  quotationEnabled: boolean;
};

export const PipelineStagesTable = (props: PipelineStagesTableProps) => {
  const { control, register, formState: { errors }, setValue, trigger } = useFormContext<Pipeline>();
  const quotationEnabled = props.quotationEnabled;

  const { fields, append: appendStage, remove: removeStage, swap: swapStages } = useFieldArray({
    name: 'stages',
    control,
    rules: {
      validate: (stages) => {
        if (!quotationEnabled) {
          return true; // If quotation is not enabled, we don't need to validate the presence of quotation and won stages
        }

        const stageList = stages ?? [];
        const hasQuotationStage = stageList.some((stage) => stage?.quotationStage);
        const hasWonStage = stageList.some((stage) => stage?.wonStage);

        return (hasQuotationStage && hasWonStage) || 'At least one Quotation Stage and one Won Stage must be selected.';
      }
    }
  });

  const handleQuotationStageChange = (index: number, checked: boolean) => {
    if (checked) {
      // Uncheck all other quotation stages
      fields.forEach((_, fieldIndex) => {
        if (fieldIndex !== index) {
          setValue(`stages.${fieldIndex}.quotationStage`, false, { shouldValidate: true, shouldDirty: true });
        }
      });

      setValue(`stages.${index}.wonStage`, false, { shouldValidate: true, shouldDirty: true });
    }

    trigger("stages");
  };

  const handleWonStageChange = (index: number, checked: boolean) => {
    if (checked) {
      // Uncheck all other won stages
      fields.forEach((_, fieldIndex) => {
        if (fieldIndex !== index) {
          setValue(`stages.${fieldIndex}.wonStage`, false, { shouldValidate: true, shouldDirty: true });
        }
      });

      setValue(`stages.${index}.quotationStage`, false, { shouldValidate: true, shouldDirty: true });
    }

    trigger("stages");
  };

  const getEnumOptions = (enumObj: any): { value: number, label: string }[] => {
    return Object.keys(enumObj)
      .filter(key => isNaN(Number(key))) // Filter out the reverse mappings
      .map(key => ({
        value: enumObj[key],
        label: key.charAt(0) + key.slice(1).toLowerCase(), // consider translation from a lookup table
      }));
  }

  const selectOptions = getEnumOptions(OpportunityState);

  const addStage = () => {
    appendStage({
      id: null,
      name: '',
      prospect: 0,
      rank: 0,
      closed: false,
      colour: '',
      defaultStatus: OpportunityState.Active,
      inUse: false,
      quotationStage: false,
      wonStage: false
    });
  };

  const deleteStage = (index: number) => {
    const stage = fields[index];
    if (stage.inUse) {
      return;
    }
    removeStage(index);
  };

  const swapStage = (index1: number, index2: number) => {
    swapStages(index1, index2);
  };

  return (
    <>
      <div className="inline-flex gap-2 items-center md:col-span-5">
        <div className="text-gray-600 w-1/2">
          <p className="font-semibold text-lg py-2">Configure Pipeline Stages</p>
        </div>
        {/* <div className="inline-flex gap-2 w-1/2 flex-row-reverse">
          <button
            type="button"
            className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold py-2 px-4 rounded"
            onClick={addStage}>Add Stage</button>
        </div> */}
      </div>
      <div className="gap-4 gap-y-2 text-sm md:col-span-5">
        <p>Add the stages of your pipeline, this is your sales pipeline as a process top to bottom.</p>
        <hr className="pb-4 mt-2" />
      </div>
      <div className="md:col-span-5">
        <ErrorLabel error={errors.stages?.root?.message} />
      </div>
      <div className="md:col-span-5">
        <table className="min-w-full divide-y-2 divide-gray-200 bg-white text-sm table-fixed">
          <thead className="text-center bg-table-header border-t border-b border-table-border">
            <tr>
              <th className="px-4 py-2 font-semibold whitespace-nowrap text-table-header-foreground"></th>
              <th className="px-4 py-2 w-6/12 font-semibold whitespace-nowrap text-table-header-foreground">Name</th>
              <th className="px-4 py-2 w-3/12 font-semibold whitespace-nowrap text-table-header-foreground">Probability %</th>
              <th className="px-4 py-2 w-3/12 font-semibold whitespace-nowrap text-table-header-foreground">Default Status</th>
              {quotationEnabled && <th className="px-4 py-2 w-3/12 font-semibold whitespace-nowrap text-table-header-foreground">Quotation Stage</th>}
              {quotationEnabled && <th className="px-4 py-2 w-3/12 font-semibold whitespace-nowrap text-table-header-foreground">Won Stage</th>}
              <th className="px-4 py-2 font-semibold whitespace-nowrap text-table-header-foreground"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {fields.map((field, index) => {
              return (
                <tr key={field.id}>
                  <td className="w-1/6 px-4 py-2 font-medium whitespace-nowrap text-gray-900">
                    <div className="inline-flex gap-2 items-center">
                      {index > 0 &&
                        <button type='button' className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold py-2 px-4 rounded" onClick={() => swapStage(index, index -1)}>
                          <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                      }

                      {index < (fields.length - 1)  &&
                        <button type='button' className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold py-2 px-4 rounded" onClick={() => swapStage(index, index + 1)}>
                          <FontAwesomeIcon icon={faArrowDown} />
                        </button>
                      }
                    </div>
                  </td>
                  <td className="px-4 py-2 font-medium whitespace-nowrap text-gray-900">
                    <Input
                      placeholder="Enter a name for your stage"
                      {...register(`stages.${index}.name`, { required: 'required', maxLength: { value: 50, message: 'max 50 characters' }, validate: validateNoHtml })}
                      inErrorState={!!errors.stages?.[index]?.name}
                    />
                    { errors.stages?.[index]?.name && <span className="text-red-500 ml-1 font-normal block">{errors.stages[index].name.message}</span> }
                  </td>
                  <td className="px-4 py-2 font-medium text-gray-900">
                    <Input
                      {...register(`stages.${index}.prospect`, {
                        valueAsNumber: true,
                        required: Translation.PipelineMaintenance.errorProbablyMustBe,
                        min: {
                          value: 0,
                          message: Translation.PipelineMaintenance.errorProbablyMustBe,
                        },
                        max: {
                          value: 100,
                          message: Translation.PipelineMaintenance.errorProbablyMustBe,
                        },
                        validate: (value, pipeline) => {
                          if (pipeline.stages[index].wonStage && value != 100) {
                            return "The stage marked as Won must have a probability of 100%.";
                          }
                          return true;
                        }
                      })}
                      type="number"
                      inErrorState={!!errors.stages?.[index]?.prospect}
                    />
                    { errors.stages?.[index]?.prospect && <span className="text-red-500 ml-1 font-normal block break-words">{errors.stages[index].prospect.message}</span> }
                  </td>
                  <td className="px-4 py-2 font-medium whitespace-nowrap text-gray-900">
                    <select
                      {...register(`stages.${index}.defaultStatus`)}
                      className="h-10 border mt-1 rounded px-4 w-full bg-gray-50 focus-visible:border-gray-400 focus:outline-none">
                      <option value=""></option>
                      {selectOptions.map((option, index) => (
                        <option key={index} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </td>
                  {quotationEnabled &&<td className="px-4 py-2 font-medium whitespace-nowrap text-gray-900">
                    <Controller
                      control={control}
                      name={`stages.${index}.quotationStage`}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            handleQuotationStageChange(index, e.target.checked);
                          }}
                        />
                      )}
                    />
                    { errors.stages?.[index]?.quotationStage && <span className="text-red-500 ml-1 font-normal block">{errors.stages[index].quotationStage.message}</span> }
                  </td>}
                  {quotationEnabled && <td className="px-4 py-2 font-medium whitespace-nowrap text-gray-900">
                    <Controller
                      control={control}
                      name={`stages.${index}.wonStage`}
                      render={({ field }) => (
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            handleWonStageChange(index, e.target.checked);
                          }}
                        />
                      )}
                    />
                    { errors.stages?.[index]?.wonStage && <span className="text-red-500 ml-1 font-normal block">{errors.stages[index].wonStage.message}</span> }
                  </td>}
                  <td className="w-1/6 px-4 py-2 font-medium whitespace-nowrap text-gray-900">
                    <button 
                      type='button' 
                      className={`font-bold py-2 px-4 rounded ${
                        field.inUse 
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                          : 'bg-kPurple-500 hover:bg-kPurple-700 text-white'
                      }`}
                      onClick={() => deleteStage(index)}
                      disabled={field.inUse}
                      title={field.inUse ? 'Cannot delete stage that is in use' : 'Delete stage'}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className="px-4 py-2 font-medium whitespace-nowrap text-gray-900 text-left bg-white border-t border-b border-table-border">
                <div className="inline-flex gap-4 items-start">
                  <button
                    type="button"
                    className="bg-kPurple-500 hover:bg-kPurple-700 text-white font-bold py-2 px-4 rounded"
                    onClick={addStage}>Add Stage</button>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};
 